import dayjs from 'dayjs';

function timeToMinutes(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

function previousDay(weekDayIdx) {
  return (weekDayIdx + 6) % 7;
}

// Fin de la « nuit » : avant cette heure, on est encore rattaché au service du
// soir de la veille. Même frontière que le repli sans horaires configurés
// (`hour <= 5`), pour que les deux chemins racontent la même histoire.
const NIGHT_END_MINUTES = 6 * 60;

/**
 * Determines the current service period (lunch or dinner) and the correct service day.
 * Handles dinner periods that cross midnight — e.g. if dinner is 18:00-02:00 and it's
 * 00:30 Tuesday, returns { isDayTime: false, serviceDay: 1 } (Monday's dinner).
 *
 * @param {Object|null} availabilityHours - Restaurant's settings.availabilityHours
 * @param {number} weekDayIdx - Day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @param {boolean} isPublicHoliday - Whether today is a public holiday
 * @returns {{ isDayTime: boolean, serviceDay: number|string }}
 */
export function getAvailabilityPeriod(availabilityHours, weekDayIdx, isPublicHoliday) {
  const now = dayjs();
  const hour = now.hour();

  // Fallback: no custom hours configured
  if (!availabilityHours) {
    if (hour > 5 && hour < 17) {
      return { isDayTime: true, serviceDay: weekDayIdx };
    }
    // Between midnight and 5h, we're in the previous day's dinner service
    if (hour <= 5) {
      return { isDayTime: false, serviceDay: previousDay(weekDayIdx) };
    }
    return { isDayTime: false, serviceDay: weekDayIdx };
  }

  const currentMinutes = hour * 60 + now.minute();
  const dayKey = isPublicHoliday ? 'publicHoliday' : String(weekDayIdx);
  const todayHours = availabilityHours.overrides?.[dayKey] || availabilityHours.default;

  if (!todayHours?.lunch) {
    if (hour > 5 && hour < 17) {
      return { isDayTime: true, serviceDay: weekDayIdx };
    }
    if (hour <= 5) {
      return { isDayTime: false, serviceDay: previousDay(weekDayIdx) };
    }
    return { isDayTime: false, serviceDay: weekDayIdx };
  }

  // Check if we're in today's lunch period
  const lunchStart = timeToMinutes(todayHours.lunch.start);
  const lunchEnd = timeToMinutes(todayHours.lunch.end);
  const inLunch = lunchStart <= lunchEnd
    ? (currentMinutes >= lunchStart && currentMinutes < lunchEnd)
    : (currentMinutes >= lunchStart || currentMinutes < lunchEnd);

  if (inLunch) {
    return { isDayTime: true, serviceDay: isPublicHoliday ? 'publicHoliday' : weekDayIdx };
  }

  // Check if we're in today's dinner period
  const dinerStart = timeToMinutes(todayHours.diner.start);
  const dinerEnd = timeToMinutes(todayHours.diner.end);
  const dinerCrossesMidnight = dinerStart > dinerEnd;

  if (dinerCrossesMidnight) {
    // Tonight's part: from dinerStart to midnight
    if (currentMinutes >= dinerStart) {
      return { isDayTime: false, serviceDay: isPublicHoliday ? 'publicHoliday' : weekDayIdx };
    }
  } else {
    if (currentMinutes >= dinerStart && currentMinutes < dinerEnd) {
      return { isDayTime: false, serviceDay: isPublicHoliday ? 'publicHoliday' : weekDayIdx };
    }
  }

  // Check if we're in yesterday's dinner period that crossed midnight
  const prevDayKey = isPublicHoliday ? 'publicHoliday' : String(previousDay(weekDayIdx));
  const prevDayHours = availabilityHours.overrides?.[prevDayKey] || availabilityHours.default;

  if (prevDayHours?.diner) {
    const prevDinerStart = timeToMinutes(prevDayHours.diner.start);
    const prevDinerEnd = timeToMinutes(prevDayHours.diner.end);

    if (prevDinerStart > prevDinerEnd && currentMinutes < prevDinerEnd) {
      return { isDayTime: false, serviceDay: isPublicHoliday ? 'publicHoliday' : previousDay(weekDayIdx) };
    }
  }

  // Hors de toute plage de service (le matin avant l'ouverture, ou le creux
  // entre midi et soir quand les deux plages ne se touchent pas).
  //
  // Renvoyer « soir » systématiquement — ce que faisait cette ligne — donnait
  // la carte du soir dès la fin du service du midi, et même le matin. La règle
  // est donc : tant que le service du soir n'a pas commencé, c'est la carte du
  // midi. Ça reproduit l'ancien comportement du repli codé en dur (tout est
  // midi avant 17h) pour les restaurants qui laissent un trou dans leurs
  // horaires, sans jamais vider la carte — « peut-on commander maintenant ? »
  // reste la responsabilité de kitchenHours, qui a son propre message.
  if (currentMinutes < dinerStart) {
    // Exception : les petites heures appartiennent encore au service du soir
    // de la veille. La frontière est fixe (NIGHT_END_MINUTES) et non
    // `lunchStart` : un restaurant qui n'ouvre son midi qu'à 11h30 ne doit pas
    // voir sa matinée rattachée à la veille.
    if (currentMinutes < NIGHT_END_MINUTES) {
      return { isDayTime: false, serviceDay: isPublicHoliday ? 'publicHoliday' : previousDay(weekDayIdx) };
    }
    return { isDayTime: true, serviceDay: isPublicHoliday ? 'publicHoliday' : weekDayIdx };
  }

  return { isDayTime: false, serviceDay: isPublicHoliday ? 'publicHoliday' : weekDayIdx };
}

/**
 * Backward-compatible wrapper — returns only the isDayTime boolean.
 */
export function isDayTimePeriod(availabilityHours, weekDayIdx, isPublicHoliday) {
  return getAvailabilityPeriod(availabilityHours, weekDayIdx, isPublicHoliday).isDayTime;
}

/**
 * Filtre produit de we-kiosk-order (inline dans Restaurant.js), factorisé :
 * - pas de champ `availability` ou `always` coché → toujours visible ;
 * - jour férié → seuls les créneaux `publicHoliday-lunch|diner` comptent ;
 * - sinon le créneau `${serviceDay}-${lunch|diner}` du jour de service.
 */
export function isProductAvailable(product, { isDayTime, serviceDay }, isPublicHoliday) {
  const availability = product.availability
  if (typeof availability === 'undefined' || availability === null) return true
  if (typeof availability['always'] !== 'undefined' && availability['always']) return true
  const period = isDayTime ? 'lunch' : 'diner'
  if (isPublicHoliday) {
    return typeof availability[`publicHoliday-${period}`] !== 'undefined' && !!availability[`publicHoliday-${period}`]
  }
  return typeof availability[`${serviceDay}-${period}`] !== 'undefined' && !!availability[`${serviceDay}-${period}`]
}
